import { SaveAnswerCommandDto } from "api/borrower";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { useUnemploymentRiskData } from "features/subscription-process/contexts/selectors/selectors";
import { questionValidationRules } from "features/subscription-process/slices/thunks/mockChomageQuestions";
import { nextStep } from "features/subscription-process/slices/thunks/nextStepThunk";
import { UnemploymentStepIdentifier } from "features/subscription-process/types/enums";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { risksAPI } from "services/swagger-api/riskApi";
import { goToFirstStep, goToNextStep, initProfilingConfig } from "../slices/genericProfilingSlice";
import { selectProfilingConfig } from "../slices/genericSelectors";
import { useGenericProfiling } from "./useGenericProfiling";
import { ContextCode, SurveyName, useGenericSurvey } from "./useGenericSurvey";

export const useUnemploymentProfiling = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { riskId } = useParams<{ riskId: string }>();
  const { getSurvey, mapFromSurveyFormValue, mapToSurveyFormValue } = useGenericSurvey()
  const riskData = useUnemploymentRiskData()
  const surveyAnswers = riskData?.surveyAnswers

  const [showForm, setShowForm] = useState(false)

  const baseHook = useGenericProfiling();
  const [isLoading, setIsLoading] = useState(false);

  const profilingConfig = useAppSelector(selectProfilingConfig);

  useEffect(() => {
    if (!profilingConfig.validationRules || profilingConfig.validationRules?.length === 0)
      dispatch(initProfilingConfig({
        ...profilingConfig,
        validationRules: questionValidationRules,
      }));
  }, [dispatch, profilingConfig, riskId]);


  useEffect(() => {
    if (surveyAnswers) {
      mapToSurveyFormValue(surveyAnswers)
      dispatch(goToFirstStep())
    }
  }, [surveyAnswers, dispatch, mapToSurveyFormValue])


  const handleSubmit = useCallback(async () => {
    try {
      if (!profilingConfig.validationRules || !riskId) {
        return;
      }

      setIsLoading(true);

      const answers = mapFromSurveyFormValue(baseHook.answers);
      const survey = await getSurvey(ContextCode.Unemployment, SurveyName.Unemployment);

      const saveAnswerCommandDto: SaveAnswerCommandDto = {
        surveyId: survey.id,
        answers
      };

      const response = await risksAPI.patchSurvey({
        riskId,
        saveAnswerCommandDto
      });

      if (response.isEligible) {
        dispatch(nextStep({
          stepIdentifier: UnemploymentStepIdentifier.Pricing,
          isValid: false,
          isCurrent: true,
        }));
        navigate(`/simulation/tarification/${riskId}`);
      } else {
        navigate('/excluded-coverage', {
          state: {
            message: "Nous sommes désolés, l'assuré n'est pas éligible à l'assurance chômage",
            coverageType: "chômage",
          }
        });
      }
    } catch (error) {
      console.error('Error submitting unemployment survey:', error);
    } finally {
      setIsLoading(false);
    }
  }, [baseHook.answers, mapFromSurveyFormValue, getSurvey, dispatch, navigate, profilingConfig.validationRules, riskId]);

  const handleNext = useCallback(() => {
    if (baseHook.isLastStep || showForm) {
      handleSubmit();
    } else {
      dispatch(goToNextStep());
    }
  }, [baseHook, dispatch, handleSubmit, showForm]);

  return {
    ...baseHook,
    showForm,
    setShowForm,
    handleNext,
    isLoading
  };
};
